import { getUploadImageKey, isVaultDataDecrypted } from '@onefootprint/core';
import type { DataIdentifier, Document, Entity } from '@onefootprint/request-types/dashboard';
import useEntityVault from '@/entities/hooks/use-entity-vault';
import useDecryptControls from '@/entity/hooks/use-decrypt-controls';
import getDocumentDataKinds from '../utils/get-document-data-kinds';

export const useDocumentField = ({ entity, seqno }: { entity: Entity; seqno?: number }) => {
  const vault = useEntityVault({
    entityAttributes: entity.data,
    entityId: entity.id,
    seqno: seqno,
  });
  const decryptControls = useDecryptControls();

  const getRelatedDisByFormValue = (document: Document): DataIdentifier[] => {
    if (!vault.data) {
      throw new Error('Vault not loaded');
    }
    const { uploads, completedVersion, kind, sambaActivityHistoryCompletedVersion, redactedAt } = document;

    const imageDis = redactedAt ? [] : uploads.map(getUploadImageKey);
    if (!completedVersion) {
      return [...new Set([...imageDis])];
    }

    const documentDataKinds = getDocumentDataKinds(vault.data.dataKinds, kind);
    const textDis = documentDataKinds.map(di => {
      const isSambaActivityDI = di.includes('samba_activity_history_response');
      const useSambaVersion = isSambaActivityDI && sambaActivityHistoryCompletedVersion;
      const version = useSambaVersion ? sambaActivityHistoryCompletedVersion : completedVersion;
      return `${di}:${version}` as DataIdentifier;
    });
    return [...new Set([...imageDis, ...textDis])];
  };

  const getProps = (document: Document) => {
    if (!vault.data) {
      throw new Error('Vault not loaded');
    }
    const vaultData = vault.data.vault;
    const isDecryptable = document.canDecrypt;
    // Redacted images never decrypt, so the extracted data is what unlocks the document
    const isDecrypted = document.redactedAt
      ? getRelatedDisByFormValue(document).some(di => isVaultDataDecrypted(vaultData[di]))
      : document.uploads.every(upload => isVaultDataDecrypted(vaultData[getUploadImageKey(upload)]));

    return {
      isDecrypting: decryptControls.isDecrypting(getRelatedDisByFormValue(document)),
      isDecryptable,
      isDecrypted,
      canSelect: isDecryptable && !isDecrypted,
      showCheckbox: decryptControls.step === 'selecting-fields' || decryptControls.step === 'selecting-reason',
      disabled: !isDecryptable || isDecrypted,
    };
  };

  return { getProps, getRelatedDisByFormValue, isVaultLoaded: Boolean(vault.data) };
};

export default useDocumentField;
